import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { QuantityStepper } from "@/components/supplement/QuantityStepper";
import ingredientsData from "@/data/supplement-check/ingredients.json";

const BASE_LABELS = Object.fromEntries(ingredientsData.ingredients.map((item) => [item.id, item.name]));
const MAGNESIUM_LABELS = {
  magnesium_glycinate: "Magnesium glycinate",
  magnesium_citrate: "Magnesium citrate",
  magnesium_malate: "Magnesium malate",
  magnesium_oxide: "Magnesium oxide",
  magnesium_l_threonate: "Magnesium L-threonate",
};
const LABELS = { ...BASE_LABELS, ...MAGNESIUM_LABELS };
const doseStep = (unit) => {
  if (unit === "IU") return 500;
  if (unit === "µg") return 50;
  if (unit === "mg EPA+DHA") return 100;
  if (unit === "g") return 1;
  return 5;
};

export const SupplementListControl = ({ question, value, onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const allowed = Object.keys(question.engine_units);
  const addRow = () => onChange([...rows, { ingredient: "", amount_per_day: "", unit: "" }]);

  const updateRow = (index, field, rawValue) => {
    const next = rows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      if (field === "ingredient") {
        return {
          ...row,
          ingredient: rawValue,
          unit: question.engine_units[rawValue] || "",
          amount_per_day: question.default_amounts?.[rawValue] ?? "",
        };
      }
      return { ...row, amount_per_day: rawValue === "" ? "" : Number(rawValue) };
    });
    onChange(next);
  };

  return (
    <div className="supplement-list" data-testid={`supplement-list-${question.id}`}>
      <label className={`supplement-none-row ${rows.length === 0 ? "is-selected" : ""}`} data-testid="supplement-list-none-label">
        <Checkbox checked={rows.length === 0} onCheckedChange={(checked) => onChange(checked ? [] : [{ ingredient: "", amount_per_day: "", unit: "" }])} data-testid="supplement-list-none-checkbox" />
        <span>None</span>
      </label>
      {rows.length > 0 && <p className="supplement-dose-note" data-testid="supplement-dose-prefill-note">Amounts start with a common label value. Check your bottle and edit it if yours is different.</p>}
      {rows.map((row, index) => (
        <div className="supplement-list-row" key={`supplement-row-${index}`} data-testid={`supplement-list-row-${index}`}>
          <label htmlFor={`supplement-ingredient-${index}`}>
            <span>Supplement</span>
            <select id={`supplement-ingredient-${index}`} value={row.ingredient} onChange={(event) => updateRow(index, "ingredient", event.target.value)} data-testid={`supplement-ingredient-select-${index}`}>
              <option value="">Choose a supplement</option>
              {allowed.map((id) => <option value={id} key={id}>{LABELS[id] || id}</option>)}
            </select>
          </label>
          <div className="supplement-amount-field">
            <span>Amount per day</span>
            <QuantityStepper id={`supplement-amount-${index}`} label={`${LABELS[row.ingredient] || "supplement"} amount per day`} value={row.amount_per_day} min={0} max={10000} step={doseStep(row.unit)} disabled={!row.ingredient} onChange={(next) => updateRow(index, "amount_per_day", next)} />
          </div>
          <span className="supplement-unit" data-testid={`supplement-unit-${index}`}>{row.unit}</span>
          <Button type="button" variant="ghost" size="icon" onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))} aria-label="Remove supplement" data-testid={`supplement-remove-row-${index}`}><Trash2 aria-hidden="true" /></Button>
        </div>
      ))}
      {rows.length > 0 && <Button type="button" variant="outline" size="icon" onClick={addRow} aria-label="Add supplement" data-testid="supplement-add-row-button"><Plus aria-hidden="true" /></Button>}
    </div>
  );
};